import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import * as requests from './requests'
import { TCartPreviewItem } from './types'

export const KEY_GET_PRODUCT_TIERS = 'getProgressiveProductTiers'
export const KEY_GET_CART_PREVIEW = 'getProgressiveCartPreview'
export const KEY_GET_PRODUCT_RELATED = 'getProgressiveProductRelated'

const useDebounced = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debouncedValue
}

export const useGetProductTiers = (productId?: number, enabled?: boolean) =>
  useQuery({
    queryKey: [KEY_GET_PRODUCT_TIERS, productId],
    queryFn: () => requests.getProductTiers(productId as number),
    enabled: !!productId && (enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5 min — faixas mudam pouco
    select: (response) => response?.data,
    meta: {
      errorMessage: 'Erro ao buscar faixas de desconto progressivo.'
    }
  })

export const useProductRelatedByRule = (productId?: number, cartProductIds?: number[], enabled?: boolean) =>
  useQuery({
    queryKey: [KEY_GET_PRODUCT_RELATED, productId, cartProductIds],
    queryFn: () => requests.getProductRelated(productId as number, undefined, cartProductIds),
    enabled: !!productId && (enabled ?? true),
    staleTime: 1000 * 60 * 5,
    select: (response) => response?.data,
    meta: {
      errorMessage: 'Erro ao buscar produtos relacionados para desconto progressivo.'
    }
  })

export const useCartProgressivePreview = (items: TCartPreviewItem[], enabled?: boolean) => {
  // debounce de 300ms: evita disparar para cada mudança de estado intermediária no carrinho
  const debouncedItems = useDebounced(items, 300)
  return useQuery({
    queryKey: [KEY_GET_CART_PREVIEW, debouncedItems],
    queryFn: () => requests.getCartPreview(debouncedItems),
    enabled: (enabled ?? true) && Array.isArray(debouncedItems) && debouncedItems.length > 0,
    staleTime: 1000 * 30, // 30s — sensível ao estado do carrinho
    // Mantém dados anteriores durante refetch para evitar flash de "carrinho vazio" ao alterar quantidade
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
    meta: {
      errorMessage: 'Erro ao calcular o desconto progressivo previsto.'
    }
  })
}
